/**
 * PromoTix-standard Seatics pre-built UI settings (TN Maps API Integration Guide v3.18).
 * Applied to both Seatics.config and Seatics.dbConfig so sandbox/production websiteConfigIds
 * render the same layout and all-in pricing without TN Portal Maps Configuration access.
 */

/**
 * Label TN renders inside the native CTA button in every ticket-group row
 * (Seatics.config.buyButtonContentHtml, TN Integration Guide v3.18). Because this
 * string does NOT contain the {{Price}} token, TN renders the per-ticket price to
 * the LEFT of the button only when it has a price to show (production behavior).
 */
/** Match TN's default button markup (MapAndLayout uses venue-ticket-list-cta-button). */
export const PROMOTIX_SEATICS_BUY_BUTTON_HTML =
  '<button type="button" class="btn btn-primary venue-ticket-list-cta-button promotix-seatics-buy-cta">Select Tickets</button>';

/** Production-aligned defaults (website config 27803 behavior). */
export const PROMOTIX_SEATICS_UI_CONFIG = Object.freeze({
  /** Desktop: map left, ticket list right (TN default; sandbox configs may set false). */
  ticketListOnRight: true,
  /** Show all-in per-ticket prices in the list ($XX/ea), not fee-only copy. */
  includeServiceFeesInTicketPrice: true,
  showAllInPricingToggle: false,
  showFeesIncludedText: false,
  showPriceBreakdownTicketList: false,
  showPriceBreakdownPrecheckout: false,
  useH1Tag: false,
  /** Two-click checkout: row click opens pre-checkout; CTA button rendered natively per row. */
  skipPrecheckoutDesktop: false,
  skipPrecheckoutMobile: false,
  /**
   * Native per-row "Select Tickets" CTA — TN renders this in every ticket-group row's
   * CTA column. No DOM injection / MutationObserver needed; survives Seatics re-renders.
   */
  buyButtonContentHtml: PROMOTIX_SEATICS_BUY_BUTTON_HTML,
});

/** Seatics.config.smallBreakpoint — its own mobile/desktop split. */
export const SEATICS_SMALL_BREAKPOINT = 991;
/** TN default for fullyShownMapHeight is "300". */
export const SEATICS_MAP_HEIGHT_MAX = 300;
export const SEATICS_MAP_HEIGHT_MIN = 160;
export const SEATICS_MAP_HEIGHT_RATIO = 0.3;

/**
 * Height of the expanded ("Hide Map") mobile map band.
 *
 * Seatics splits the viewport between this band and the ticket list, so the TN
 * default of 300px leaves almost nothing for tickets on a short viewport. Measured
 * on staging in the attendee WebView (innerHeight 610), expanded map, with the
 * chrome box correctly sized — the relationship is linear, 1px off the map is 1px
 * back to the list:
 *
 *   300 (default) ->  67px of list,  0 rows fully visible
 *   260           -> 107px,          1 row
 *   220           -> 147px,          1 row
 *   180           -> 187px,          2 rows
 *   140           -> 227px,          3 rows
 *
 * Scaling with the viewport keeps a usable ticket band on short screens while tall
 * screens still get the full 300px map.
 *
 * @returns {number|null} px height, or null when this viewport should keep TN's default.
 */
export const mobileFullyShownMapHeight = (viewportHeight, viewportWidth) => {
  const w = Number(viewportWidth);
  if (Number.isFinite(w) && w > SEATICS_SMALL_BREAKPOINT) return null;
  const h = Number(viewportHeight);
  if (!Number.isFinite(h) || h <= 0) return null;
  return Math.max(
    SEATICS_MAP_HEIGHT_MIN,
    Math.min(SEATICS_MAP_HEIGHT_MAX, Math.round(h * SEATICS_MAP_HEIGHT_RATIO))
  );
};

/**
 * @param {Window['Seatics']} seatics
 * @param {Window} [win] viewport source; omit to use the global window.
 */
export const applyPromotixSeaticsUiConfig = (
  seatics,
  win = typeof window !== 'undefined' ? window : null
) => {
  if (!seatics || typeof seatics !== 'object') return;
  const mapHeight = win ? mobileFullyShownMapHeight(win.innerHeight, win.innerWidth) : null;
  [seatics.config, seatics.dbConfig].forEach(target => {
    if (!target || typeof target !== 'object') return;
    Object.entries(PROMOTIX_SEATICS_UI_CONFIG).forEach(([key, value]) => {
      target[key] = value;
    });
    // Seatics stores this as a string ("300").
    if (mapHeight !== null) target.fullyShownMapHeight = String(mapHeight);
  });
};
